import { useCallback, useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { fetch } from '../../../utils/api-wrapper';
import { mapTransactionsData, seperateMatchedAndUnmatchedExpenses } from '../utils';
import { type } from '@testing-library/user-event/dist/cjs/utility/type.js';

export default function useCreditCardStatement(statementId, t, navigate) {
  const [loading, setLoading] = useState(true);
  const [statement, setStatement] = useState(null);
  const [mappedTransactions, setMappedTransactions] = useState([]);
  const [unmatchedExpenses, setUnmatchedExpenses] = useState([]);
  const [allExpensesCompleted, setAllExpensesCompleted] = useState(false);
  const [categories, setCategories] = useState([]);
  const isMountedRef = useRef(true);

  const recomputeDerived = useCallback(
    (statement) => {
      if (!isMountedRef.current) return;
      const terminalStatuses = new Set(['completed', 'need_attention']);
      const expenses = statement?.expenses || [];
      const completed = expenses.length < 0 || expenses?.every((e) => e?.status && terminalStatuses.has(String(e.status).toLowerCase().trim()));
      setAllExpensesCompleted(completed);

      setMappedTransactions(
        mapTransactionsData(
          statement,
          t,
          navigate,
          (txToUpdate) => safeUpdateTransaction(txToUpdate, statement),
          (transaction) => removeExpense(transaction, statement),
          categories.map((category) => ({ name: category.name, checksum: category._id, supplier: category.supplier }))
        )
      );

      const { unmatchedExpenses: ue } = seperateMatchedAndUnmatchedExpenses(statement);
      setUnmatchedExpenses(ue);
    },
    [t, navigate, categories]
  );

  const fetchStatement = useCallback(() => {
    if (mappedTransactions.length === 0 && isMountedRef.current) {
      setLoading(true);
    }
    return new Promise((resolve) => {
      fetch(
        `/c_card/statement/?statement_id=${statementId}`,
        'GET',
        {},
        (response) => {
          if (!isMountedRef.current) {
            resolve(null);
            return;
          }
          const statement = response?.data?.statement;
          if (!statement) {
            toast.error(t.read('cc_transactions.error_fetching_statement') || 'Error fetching statement');
            setLoading(false);
            resolve(null);
            return;
          }
          setStatement(statement);
          recomputeDerived(statement);
          setLoading(false);
          resolve(statement);
        },
        () => {
          if (!isMountedRef.current) {
            resolve(null);
            return;
          }
          toast.error(t.read('cc_transactions.error_fetching_statement') || 'Error fetching statement');
          setLoading(false);
          resolve(null);
        }
      );
    });
  }, [statementId, t, recomputeDerived, mappedTransactions.length]);

  const saveTransactionUpdate = useCallback(
    (transaction) => {
      fetch(
        `/c_card/transaction/${statementId}/${transaction.transaction_id}`,
        'PUT',
        transaction,
        () => {},
        () => {
          toast.error(t.read('cc_transactions.error_updating_transaction') || 'Error updating transaction');
        }
      );
    },
    [statementId, t]
  );

  const safeUpdateTransaction = useCallback(
    (updatedTransaction, currentStatement) => {
      if (!isMountedRef.current) return;
      const activeStatement = currentStatement || statement;
      if (!activeStatement) {
        toast.error(t.read('cc_transactions.error_null_statement') || 'Cannot update: statement is null');
        return;
      }
      const updated = { ...activeStatement };
      const idx = (updated.transactions || []).findIndex((tx) => tx.transaction_id === updatedTransaction.transaction_id);
      if (idx === -1) {
        toast.error(t.read('cc_transactions.transaction_not_found') || 'Transaction not found');
        return;
      }
      updated.transactions[idx] = updatedTransaction;
      setStatement(updated);
      recomputeDerived(updated);
      saveTransactionUpdate(updatedTransaction);
    },
    [statement, t, recomputeDerived, saveTransactionUpdate]
  );

  const removeExpense = useCallback(
    (transaction, currentStatement) => {
      const activeStatement = currentStatement || statement;
      if (!activeStatement) {
        return;
      }

      fetch(
        `/plaid/transactions/decouple/${transaction.transaction_id}`,
        'POST',
        {},
        (response) => {
          if (!isMountedRef.current) return;
          const updated = { ...activeStatement };
          const index = updated.transactions.findIndex((t) => t.transaction_id === transaction.transaction_id);
          if (index !== -1 && response?.data?.transaction) {
            updated.transactions[index].checksum = '';
            updated.transactions[index].json = {};
            updated.transactions[index].readyForSubmit = false;
            updated.transactions[index].submitted = false;
          } else {
            updated.transactions = updated.transactions.filter((t) => t.transaction_id !== transaction.transaction_id);
          }

          setStatement(updated);
          recomputeDerived(updated);
        },
        () => {
          if (!isMountedRef.current) return;
          toast.error(t.read('cc_transactions.error_removing_expense') || 'Error removing expense');
        }
      );
    },
    [statement, t, recomputeDerived]
  );

  const deleteExpense = useCallback(
    (expenseChecksum) => {
      if (!statement) return;
      const expense = statement.expenses?.find((e) => e.checksum === expenseChecksum);
      if (!expense) {
        toast.error(t.read('cc_transactions.expense_not_found') || 'Expense not found');
        return;
      }

      fetch(
        `/c_card/expenses/${statement._id}/${expenseChecksum}`,
        'DELETE',
        {},
        (response) => {
          if (!isMountedRef.current) return;
          const updated = { ...statement };
          updated.expenses = (updated.expenses || []).filter((e) => e.checksum !== expenseChecksum);
          updated.transactions = (updated.transactions || []).map((tx) => (tx.checksum === expenseChecksum ? { ...tx, checksum: '' } : tx));
          setStatement(updated);
          recomputeDerived(updated);
          toast.success(t.read('cc_transactions.expense_deleted') || 'Receipt deleted');
        },
        () => {
          if (!isMountedRef.current) return;
          toast.error(t.read('cc_transactions.error_deleting_expense') || 'Error deleting receipt');
        }
      );
    },
    [statement, t, recomputeDerived]
  );

  const handleExpenseDrop = useCallback(
    (expense, transaction) => {
      if (!statement) return;

      if (transaction.checksum) {
        toast.error(t.read('cc_transactions.transaction_already_matched') || 'Transaction already has a matched expense');
        return;
      }

      const actualTransaction = statement.transactions.find(
        (statementTransaction) =>
          statementTransaction.transaction_id === transaction.transaction_id ||
          (statementTransaction.amount === transaction.amount.replace('$', '') &&
            statementTransaction.date === transaction.date &&
            statementTransaction.merchant_name === transaction.merchant_name)
      );

      if (!actualTransaction) {
        toast.error(t.read('cc_transactions.transaction_not_found') || 'Transaction not found');
        return;
      }

      const transactionJson = expense.json;
      transactionJson.type = expense.type || actualTransaction.type || 'p-card';
      

      const updatedTransaction = {
        ...actualTransaction,
        json: transactionJson,
        checksum: expense.checksum,
        type: expense.type || actualTransaction.type || 'p-card',
      };


      const invoice_id = expense.invoice_id || null;
      if (invoice_id) {
        updatedTransaction.invoice_id = invoice_id;
        updatedTransaction.submitted = true;
      }

      safeUpdateTransaction(updatedTransaction, statement);
    },
    [statement, t, safeUpdateTransaction]
  );

  const fetchCategories = useCallback(() => {
    fetch(
      `/c_card_category/search/?get_all=${true}`,
      'GET',
      {},
      (response) => {
        if (!isMountedRef.current) return;
        const data = response.data;
        setCategories(data);
      },
      () => {}
    );
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchStatement();
  }, [fetchStatement]);

  return {
    statement,
    loading,
    mappedTransactions,
    unmatchedExpenses,
    allExpensesCompleted,
    fetchStatement,
    handleExpenseDrop,
    deleteExpense,
  };
}
